import {
  CwsA,
  CwsButton,
  CwsCard,
  CwsCardBody,
  CwsCardImg,
  CwsGrid,
  CwsGridCol,
  CwsGridRow,
  CwsP,
  CwsSection,
  CwsAlignerGroup,
} from '@components/cws'
import { ProductImage, Rating } from '@components/product'
import { ProductTagsRow } from '@components/product/ProductCard'
import { useAlert } from '@context/alert'
import { useInitialData } from '@context/initialDataContext'
import { useAppState } from '@context/state'
import { useSpecialPrice } from '@hooks/product'
import { useBundleLabel } from '@hooks/useBundleLabel'
import { useCart } from '@hooks/useCart'
import { useCurrency } from '@hooks/useCurrency'
import { usePriceInfo } from '@hooks/usePriceInfo'
import { useTranslate } from '@hooks/useTranslation'
import { useWishlist } from '@hooks/useWishlist'
import { CartEvent, gaEventCart, UserData } from '@lib/gtm'
import type { Locale } from '@model/locales'
import { KK_LANG_IDS } from '@model/locales'
import type { Product } from '@model/product'
import { getPriceValueWithCurrency } from '@utils/currency'
import { getCommonGaDto } from '@utils/getCommonGaDto'
import {
  getCustomAttrCustomFacetCampaign,
  getCustomAttrCustomFacetLabel,
} from '@utils/getCustomAttrCustomFacetCampaign'
import { getFullImageUrl } from '@utils/getFullImageUrl'
import { getProductTitle } from '@utils/getProductTitle'
import { getFormattedPrice } from '@utils/price'
import { isGloballyPricelist } from '@utils/pricelist-utils/app-properties'
import { oncePerBasket } from '@utils/product-utils'
import { cartIncludesGalaTicket } from '@utils/product/cartIncludesGalaTicket'
import { getPromotionDF } from '@utils/product/getPromotionDF'
import { isAlcoholicProduct } from '@utils/product/isAlcoholicProduct'
import { getFreeProductInfo } from '@utils/product/old-price-utils'
import { enhanceOriginalProduct } from '@utils/product/product-info'
import { getSlug } from '@utils/slugs'
import { AppRoutes } from 'app/routes'
import clsx from 'clsx'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as R from 'ramda'
import type { DetailedHTMLProps, FC, HTMLAttributes, PropsWithChildren } from 'react'
import { useCallback, useMemo } from 'react'
import type { ProductItemBaseProps } from '../types'
import s from './ProductCard.module.scss'
import { checkOutOfStock } from '@utils/checkOutOfStock'
const ProductCard: FC<ProductItemBaseProps> = (props) => {
  const { product, listName, index } = props

  const {
    manufacturerName,
    name: title,
    rating: ratingValue = 0,
    sku,
    id,
    image: imageRelativePath,
    customAttrArray: customAttrs,
    numberReviews,
    quantity,
    promotionResults,
  } = product

  const {
    locale,
    query: { catalogId = '' },
  } = useRouter()

  const {
    state: { customer },
  } = useAppState()

  const {
    initialData: { allPromotions, currencies },
  } = useInitialData()

  const isPricelist = isGloballyPricelist()
  const priceInfo = usePriceInfo(product, allPromotions)
  const enhancedProduct = enhanceOriginalProduct(product, KK_LANG_IDS[locale as Locale], allPromotions)
  const image = useMemo(() => getFullImageUrl(imageRelativePath?.replace('_big', '_medium')), [imageRelativePath])

  const { showAlert } = useAlert()
  const { addToCartWithSideEffects } = useCart(isPricelist ? 'shopping list' : 'shopping cart')
  const { addToWishlist, wishlist } = useWishlist()

  const handleAddAction = async () => {
    if (isPricelist) {
      const alreadyAdded = R.compose(R.propOr(0, 'quantityDesired'), R.find(R.propEq('productId', id)))(wishlist)
      const quantityDesired = 1 + (alreadyAdded as number)

      const added = await addToWishlist({
        productId: id,
        product: {
          sku,
        },
        quantityDesired,
      })
      gaEventAddToWishlist(product)

      if (added) {
        showAlert({ text: translate('s2.wishlist.items.added') as string, hideDelay: 4000 })
      } else {
        showAlert({ error: true, text: translate('basket.items.add.error.general') as string, hideDelay: 4000 })
      }
    } else {
      await addToCartWithSideEffects({ productId: id, sku, quantity: 1 })
      gaEventAddToCart(product)
    }
  }

  const calculatedTitle = getProductTitle(product)

  const { translate } = useTranslate()

  const LinkWrap = useCallback(
    ({
      children,
    }: PropsWithChildren<{
      anchorProps?: DetailedHTMLProps<HTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
    }>) => {
      const productSlug = getSlug(title, sku)
      const href = catalogId
        ? `/${catalogId}${AppRoutes.productDetails.get(productSlug)}`
        : AppRoutes.productDetails.get(productSlug)
      return (
        <Link href={href} passHref legacyBehavior>
          <CwsA
            hideArrow
            href={href}
            textAlign="center"
            onClick={() => {
              gaEventSelectItem()
            }}
          >
            <CwsGridRow noGutter justifyContent="center">
              <CwsGridCol col="auto">{children}</CwsGridCol>
            </CwsGridRow>
          </CwsA>
        </Link>
      )
    },
    [sku, title, catalogId]
  )

  const LinkWrapWithoutAnchor = useCallback(
    ({ children }: PropsWithChildren) => {
      const productSlug = getSlug(title, sku)

      const href = catalogId
        ? `/${catalogId}${AppRoutes.productDetails.get(productSlug)}`
        : AppRoutes.productDetails.get(productSlug)
      return (
        <Link href={href} passHref legacyBehavior>
          <CwsA
            href={href}
            color="buoy-dark"
            textAlign="center"
            className="cws-py-xs"
            onClick={() => {
              gaEventSelectItem()
            }}
          >
            {children}
          </CwsA>
        </Link>
      )
    },
    [sku, title, catalogId]
  )

  const campaign = getCustomAttrCustomFacetCampaign(customAttrs)
  const label = getCustomAttrCustomFacetLabel(customAttrs)

  const { promotionId: giftPromotionId } = getFreeProductInfo(product)
  const bundleLabel = useBundleLabel(priceInfo, campaign, translate)

  const isOutOfStock = !(product.sku.search('VAR') > 1) && checkOutOfStock(product, quantity)

  const { currency } = useCurrency()

  let { promoPrice } = enhancedProduct
  const { normalPrice, topPrice, bundleType, bundlePrice } = priceInfo
  const discountedPrice = normalPrice > topPrice ? topPrice : 0

  const hideNormalPrice = isAlcoholicProduct(product.custom3)

  const { cart: cartItems = [] } = useCart('shopping cart')

  const isGalaTicketInBasket = useMemo(
    () => oncePerBasket(sku) && Array.isArray(cartItems) && cartIncludesGalaTicket(cartItems),
    [cartItems, sku]
  )

  if (promoPrice) promoPrice = getPriceValueWithCurrency(promoPrice, currency, currencies)

  let specialPrice = (useSpecialPrice(product) && priceInfo.specialPrice) || promoPrice
  if (discountedPrice && !specialPrice) specialPrice = discountedPrice

  const isActiveCampaign = !isGloballyPricelist()
    ? campaign && !!campaign.length
    : campaign && !!campaign.length && (!!specialPrice || !!bundlePrice)

  const gaEvent = (product: Product, eventName: CartEvent) => {
    const productUrlArr = product.url.split('/')
    const discount = normalPrice - (specialPrice || normalPrice)
    const category = productUrlArr[1]
    const category2 = productUrlArr[2]
    const commonDto = getCommonGaDto(product, allPromotions, bundleLabel, translate)

    const item = {
      ...commonDto,
      price: normalPrice.toString(),
      discount: discount ? discount.toFixed(2).toString() : '',
      currency: currency,
      item_category: category ?? '',
      item_category2: category2 ?? '',
      item_list_id: listName,
      index: index ?? 1,
      quantity: '1',
    }

    gaEventCart(eventName, item, customer, specialPrice || normalPrice)
  }
  const gaEventAddToCart = (product: Product) => {
    gaEvent(product, 'add_to_cart')
  }
  const gaEventAddToWishlist = (product: Product) => {
    gaEvent(product, 'add_to_wishlist')
  }
  const gaEventSelectItem = () => {
    gaEvent(product, 'select_item')
  }

  const specialPriceKey = getPromotionDF(product.promotionResults)

  return (
    <>
      <CwsSection
        className={clsx('cws-pb-s', isOutOfStock && 'opacity-50 pointer-events-none select-none')}
        variant="div"
      >
        <CwsAlignerGroup group="group-1">
          <ProductTagsRow
            giftPromotionId={giftPromotionId}
            campaign={isActiveCampaign ? campaign : undefined}
            label={label}
            bundleLabel={isPricelist ? bundleLabel : bundleType}
            promotionResults={promotionResults}
          />
        </CwsAlignerGroup>
      </CwsSection>

      <CwsCard>
        <CwsCardImg className={isOutOfStock && 'opacity-50 pointer-events-none select-none'}>
          <LinkWrap>
            {imageRelativePath ? (
              <ProductImage
                src={image}
                alt={product.sku}
                className="aspect-[2/1]"
                product={product}
                transformations="big"
              />
            ) : (
              <ProductImage
                src="/images/placeholder.svg"
                alt="product_image"
                className="aspect-[2/1]"
                product={product}
                transformations="big"
              />
            )}
          </LinkWrap>
        </CwsCardImg>
        <CwsCardBody>
          <CwsAlignerGroup group="group-2" className={isOutOfStock && 'opacity-50 pointer-events-none select-none'}>
            <LinkWrap>
              <CwsP data-testid="product-manufacturer" className="cws-mb-4" hideSpacing textAlign="center">
                {manufacturerName}
              </CwsP>
              {calculatedTitle}
            </LinkWrap>

            {!!ratingValue && (
              <CwsGridRow justifyContent="center" noGutter>
                <CwsGridCol col="auto" className={clsx('cws-mb-s', !!ratingValue && 'cws-mt-tiny')}>
                  <Rating value={ratingValue} hideNumber={!numberReviews} number={numberReviews} />
                </CwsGridCol>
              </CwsGridRow>
            )}
          </CwsAlignerGroup>

          <CwsAlignerGroup group="group-3" className={s.aligner}>
            <CwsGrid
              className={clsx('cws-py-s', isOutOfStock && 'opacity-50 pointer-events-none select-none')}
              width="fluid"
              verticalAlign="bottom"
            >
              <CwsGridRow noGutterVertical>
                <CwsGridCol col="12" xs="12">
                  <CwsGridRow justifyContent="space-between">
                    <CwsGridCol col="auto">
                      <CwsP hideSpacing size="small">
                        {specialPrice ? translate(specialPriceKey) : translate('w2.product.tile.price.normal')}
                      </CwsP>
                    </CwsGridCol>

                    <CwsGridCol col="auto">
                      <CwsGridRow noGutter>
                        <CwsGridCol col="auto">
                          <CwsP hideSpacing weight="500" color={specialPrice ? 'pink' : 'midnight'}>
                            {getFormattedPrice(specialPrice ? specialPrice : normalPrice, currency)}
                          </CwsP>
                        </CwsGridCol>
                      </CwsGridRow>
                    </CwsGridCol>
                  </CwsGridRow>
                </CwsGridCol>
                {!!specialPrice && !hideNormalPrice && (
                  <CwsGridCol col="12" xs="12">
                    <CwsGridRow justifyContent="space-between">
                      <CwsGridCol col="auto">
                        <CwsP color="twilight" hideSpacing size="small">
                          {translate('wishlist.email.price.normal')}
                        </CwsP>
                      </CwsGridCol>
                      <CwsGridCol col="auto">
                        <CwsP color="twilight" hideSpacing weight="500">
                          {getFormattedPrice(normalPrice, currency)}
                        </CwsP>
                      </CwsGridCol>
                    </CwsGridRow>
                  </CwsGridCol>
                )}
              </CwsGridRow>
            </CwsGrid>
          </CwsAlignerGroup>

          {isPricelist && (
            <>
              <CwsButton disabled={isGalaTicketInBasket} onClick={handleAddAction} variant="secondary" width="fluid">
                {translate('product.tile.add.to.shopping.list')}
              </CwsButton>
              <LinkWrapWithoutAnchor>{translate('order.history.details.open.details')}</LinkWrapWithoutAnchor>
            </>
          )}
          {!isPricelist && (
            <>
              {isOutOfStock ? (
                <>
                  <CwsButton variant="secondary" size="medium" width="fluid" disabled>
                    {translate('product.tile.out.of.stock')}
                  </CwsButton>
                  <LinkWrapWithoutAnchor>{translate('order.history.details.open.details')}</LinkWrapWithoutAnchor>
                </>
              ) : (
                <>
                  <CwsButton
                    disabled={isGalaTicketInBasket}
                    onClick={handleAddAction}
                    className="add-to-cart-btn"
                    variant="secondary"
                    width="fluid"
                  >
                    {translate('common.add.to.cart')}
                  </CwsButton>
                  <LinkWrapWithoutAnchor>{translate('order.history.details.open.details')}</LinkWrapWithoutAnchor>
                </>
              )}
            </>
          )}
        </CwsCardBody>
      </CwsCard>
    </>
  )
}

export default ProductCard
